import {
  InteractiveContainer,
  PlainButton,
  ColorPickerPopover,
  ColorPickerPopoverProps,
} from "@jugl-web/ui-kit";
import { cx, useTranslations } from "@jugl-web/utils";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { colord } from "colord";
import {
  FC,
  forwardRef,
  HTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  useMemo,
} from "react";
import { ReactComponent as RemoveIcon } from "./assets/remove.svg";
import { ReactComponent as ReorderIcon } from "./assets/reorder.svg";

interface RootProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  startSlot?: ReactNode;
  endSlot?: ReactNode;
  isFocused?: boolean;
  isInvalid?: boolean;
  className?: string;
  onClick?: () => void;
}

const Root = forwardRef<HTMLDivElement, RootProps>(
  (
    {
      children,
      startSlot,
      endSlot,
      isFocused,
      isInvalid,
      onClick,
      className,
      ...props
    },
    ref
  ) => (
    <InteractiveContainer
      ref={ref}
      className={cx(
        "group flex h-[52px] items-center justify-between overflow-hidden rounded-[10px] border border-solid border-grey-400 bg-white px-4 outline-2 outline-transparent transition-all hover:border-primary-200",
        "focus-visible:border-primary-200 focus-visible:outline-primary-200",
        isFocused && "border-primary-200 outline-primary-200",
        isInvalid &&
          "border-gradients-danger outline-gradients-danger hover:border-gradients-danger",
        className
      )}
      isDisabled={!onClick}
      onClick={onClick}
      {...props}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {startSlot}
        <div className="min-w-0 flex-1 truncate">{children}</div>
        {endSlot}
      </div>
    </InteractiveContainer>
  )
);

interface LabelProps {
  children: ReactNode;
  className?: string;
}

const Label: FC<LabelProps> = ({ children, className }) => (
  <span
    className={cx("truncate font-secondary text-base text-dark", className)}
  >
    {children}
  </span>
);

interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  onChange: (value: string) => void;
  onAccept?: () => void;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ onChange, onAccept, ...props }, ref) => (
    <input
      ref={ref}
      type="text"
      className="w-full border-0 bg-transparent p-0 font-secondary text-base text-dark outline-none"
      onChange={(event) => onChange(event.target.value)}
      onBlur={(event) => {
        if (
          event.relatedTarget instanceof HTMLElement &&
          (event.relatedTarget.dataset.removeButton ||
            event.relatedTarget.dataset.colorBox)
        ) {
          return;
        }

        onAccept?.();
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          onAccept?.();
        }
      }}
      {...props}
    />
  )
);

interface ColorBoxProps
  extends Pick<ColorPickerPopoverProps, "color" | "onColorChange"> {
  text?: string;
  isDisabled?: boolean;
}

const ColorBox: FC<ColorBoxProps> = ({ text, isDisabled, ...props }) => {
  const { t } = useTranslations();

  const { color: backgroundColor } = props;

  const textColor = useMemo(
    () =>
      colord(backgroundColor).brightness() < 0.6
        ? "#ffffff"
        : colord(backgroundColor).darken(0.7).toHex(),
    [backgroundColor]
  );

  return (
    <ColorPickerPopover
      title={t({
        id: "tasks-page.change-color",
        defaultMessage: "Change color",
      })}
      renderTrigger={({ Trigger, triggerRef }) => (
        <Trigger
          ref={triggerRef}
          className={cx(
            "jugl__focusable-outline flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border-0 bg-transparent transition",
            !isDisabled && "cursor-pointer hover:scale-110"
          )}
          style={{ backgroundColor }}
          disabled={isDisabled}
          data-color-box
        >
          {text && (
            <span
              className="text-[10px] font-medium transition-colors"
              style={{ color: textColor }}
            >
              {text}
            </span>
          )}
        </Trigger>
      )}
      {...props}
    />
  );
};

interface RemoveButtonProps {
  onClick: () => void;
}

const RemoveButton: FC<RemoveButtonProps> = ({ onClick }) => (
  <PlainButton
    className="hidden shrink-0 rounded-lg p-1 text-dark-500 transition group-hover:block hover:bg-grey-100"
    onClick={(event) => {
      event.stopPropagation();
      onClick();
    }}
    data-remove-button
  >
    <RemoveIcon />
  </PlainButton>
);

interface DraggableContainerProps {
  id: string;
  index: number;
  children: ReactNode;
  isDragDisabled?: boolean;
  className?: string;
  dragHandleClassName?: string;
}

const DraggableContainer: FC<DraggableContainerProps> = ({
  id,
  index,
  children,
  isDragDisabled,
  className,
  dragHandleClassName,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled: isDragDisabled,
    data: { index },
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        opacity: isDragging ? 0 : undefined,
        transform: CSS.Translate.toString(transform),
        transition,
      }}
      className={cx("group relative", className)}
    >
      {!isDragDisabled && (
        <PlainButton
          className={cx(
            "absolute top-1/2 -left-0.5 -translate-x-full -translate-y-1/2 opacity-0 group-hover:opacity-100",
            dragHandleClassName
          )}
          onClick={(event) => event.stopPropagation()}
          {...attributes}
          {...listeners}
        >
          <ReorderIcon />
        </PlainButton>
      )}
      {children}
    </div>
  );
};

export const TaskFieldSidebarListItemLayout = {
  Root,
  Label,
  Input,
  ColorBox,
  RemoveButton,
  DraggableContainer,
};
